import type { ParsedUrlQuery } from "querystring";
import { EntryDetail } from "@ekafyi/viteui-lib/components";
import type { GetStaticProps, GetStaticPaths } from "next";
import { useEffect } from "react";
import type { ReactElement } from "react";
import { NoteSectionLayout } from "@/components/layouts";
import { MDXRenderer } from "@/components/mdx";
import { NotesPageSeo } from "@/components/PageSeo";
import { parseMDXWithTOC } from "@/lib/mdx";
import { prismaClient } from "@/lib/prisma-client";
import { fooResolver, barResolver } from "@/lib/server-queries";

interface MoteSinglePageProps extends Awaited<ReturnType<typeof parseMDXWithTOC>> {
  pageSlug: string;
  pageData: Exclude<Awaited<ReturnType<typeof fooResolver>>, null>;
  // fruit: "apple" | "banana";
}

interface MoteSinglePageCtxParams extends ParsedUrlQuery {
  slug: string;
}

const MoteSinglePage = ({ pageData, mdxSource, toc }: MoteSinglePageProps) => {
  // Update html "lang" attribute for SEO & a11y.
  useEffect(() => {
    document.documentElement.setAttribute("lang", pageData.lang || "en");
  }, [pageData.lang]);

  const seoData = {
    title: pageData.title,
    slug: pageData.slug,
    excerpt: pageData.excerpt || undefined,
  };

  return (
    <>
      <NotesPageSeo activeData={seoData} />
      <EntryDetail data={pageData} toc={toc}>
        <MDXRenderer source={mdxSource} />
      </EntryDetail>
    </>
  );
};

MoteSinglePage.getLayout = function getLayout(page: ReactElement<MoteSinglePageProps>) {
  return <NoteSectionLayout activeSlug={page.props.pageSlug}>{page}</NoteSectionLayout>;
};

export const getStaticProps: GetStaticProps<MoteSinglePageProps, MoteSinglePageCtxParams> = async ({
  params,
  // req, res
}) => {
  // "slug" is guaranteed to exist, but these keep TS happy.
  const slug = params?.slug;
  if (!slug) return { notFound: true };

  // !! update resolver when done
  const data = await fooResolver({}, { slug }, { prisma: prismaClient });
  if (!data) return { notFound: true };

  const { content, ...restData } = data;
  const { mdxSource, toc } = await parseMDXWithTOC(content);
  const pageData = JSON.parse(JSON.stringify({ ...restData, content: "" }));

  return {
    props: {
      pageData,
      pageSlug: slug,
      mdxSource,
      toc,
      // fruit: (slug.startsWith("a") ? "apple" : "banana") as "apple" | "banana",
    },
    revalidate: 3600, // 60 * 60 seconds
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  // !! update resolver when done
  const data = await barResolver({}, {}, { prisma: prismaClient });

  return {
    paths: data.map((item) => ({ params: item })),
    fallback: "blocking",
  };
};

export default MoteSinglePage;
